import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ModalService } from 'src/app/_shared/components/modal/modal.service';
import { PaymentLinkService } from 'src/app/modules/indication/services/payment-link.service';
import { WalletService } from 'src/app/modules/indication/services/wallet.service';
import { WalletModalComponent } from './modals/wallet-modal/wallet-modal.component';
import {
  WalletTransactionsService,
  WalletTransactionType,
  TransactionStatus,
  AnalyticReportDto,
  AnalyticReportFixed,
} from 'src/app/modules/indication/services/wallet-transactions.service';
import { Account, AccountV2Service } from 'src/app/services/account-v2.service';
import { UiIndicationLaudosModalComponent } from 'src/app/shared/components/ui-indication-laudos-modal/ui-indication-laudos-modal.component';

@Component({
  selector: 'app-wallet',
  templateUrl: './wallet.component.html',
  styleUrls: ['./wallet.component.scss'],
})
export class WalletComponent implements OnInit {
  form: FormGroup;
  account: Account;
  accountId: number;
  totalAmount: number;
  generatedLink: string;
  maxLimit: number = 10;

  btnShowMore: boolean;
  btnShowMoreAnalytic: boolean = false;

  walletTransactionType = WalletTransactionType;
  transactionStatus = TransactionStatus;

  analyticReportListFull: AnalyticReportFixed[] = [];
  analyticReportList: AnalyticReportFixed[] = [];

  isLoading = {
    message: 'Carregando dados...',
    active: true,
  };
  csvLoading: boolean = false;
  private readonly modalService = inject(ModalService);

  constructor(
    private readonly accountService: AccountV2Service,
    private readonly walletService: WalletService,
    private readonly paymentService: PaymentLinkService,
    private readonly formBuilder: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly transactions: WalletTransactionsService
  ) {}

  ngOnInit(): void {
    this.getUserWalletAmmount();
    this.getAnalyticReport();

    this.form = this.formBuilder.group({
      amount: [null, Validators.required],
    });

    const localStorageInfos = localStorage.getItem('account-info');
    if (localStorageInfos) {
      const objectInfo = JSON.parse(localStorageInfos);
      this.accountId = objectInfo.accountId;
    }

    document.addEventListener('visibilitychange', this.refreshComponent);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.refreshComponent);
  }

  onSubmit() {
    const isInvalid = this.paymentService.isAmountInvalid(
      this.form.controls.amount.value
    );
    if (!this.form.valid || isInvalid) {
      this.form.controls.amount.setErrors({
        invalid: true,
      });
      throw new Error('Formulário inválido.');
    }

    const formValue = this.form.value.amount;
    const amountNumber = parseFloat(
      formValue.replace(/\.(?=\d{3})/g, '').replace(',', '.')
    );

    const dialog = this.dialog.open(WalletModalComponent, {
      data: {
        totalAmount: amountNumber,
      },
    });
    dialog.componentInstance.response$
      .pipe(
        takeUntil(dialog.afterClosed()),
        tap((data) => {
          if (data.confirm) {
            this.form.reset();
            this.generateDepositWalletBillingLink(amountNumber, data.method);
          } else {
            this.form.reset();
          }
        })
      )
      .subscribe();
  }

  openLaudosModal(item: AnalyticReportFixed): void {
    if (!item.documentUrl) return;

    this.modalService.open({
      component: UiIndicationLaudosModalComponent,
      data: {
        indicationId: item.indicationId,
        patientName: item.patientName ?? '',
        cpf: '',
      },
      width: '640px',
      maxWidth: 'calc(100vw - 32px)',
    });
  }

  showMore(): void {
    const current = this.analyticReportList.length;
    const next = current + this.maxLimit;
    this.analyticReportList = this.analyticReportListFull.slice(0, next);
    this.btnUpdate();
  }

  async downloadCsv() {
    this.csvLoading = true;
    this.transactions.downloadCsv().subscribe((blob) => {
      const link = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link;
      const date = new Date();
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      a.download = `extrato-analitico-${day}-${month}-${year}.csv`;
      a.click();
      window.URL.revokeObjectURL(link);
      this.csvLoading = false;
    });
  }

  async amountChange() {
    const isInvalid = this.paymentService.isAmountInvalid(
      this.form.controls.amount.value
    );
    if (isInvalid) {
      this.form.controls.amount.setErrors({
        invalid: true,
      });
    } else {
      this.form.controls.amount.setErrors(null);
    }
  }

  amountFormat() {
    let amount = this.form.controls['amount'].value;

    if (amount) {
      const formattedValue = Number(
        amount.replace(/\.(?=\d{3})/g, '').replace(',', '.')
      ).toFixed(2);
      this.form.controls['amount'].setValue(formattedValue.replace('.', ','));
    }
  }

  private getAnalyticReport(): void {
    this.transactions.analyticReportTransactions().subscribe({
      next: (data) => {
        if (data.length <= 0) {
          this.isLoading = {
            message: 'Nenhuma movimentação encontrada.',
            active: true,
          };
          return;
        }
        const list = this.fixResponseStructure(data);
        for (let l of list) {
          const indication = l.indicationId > 0;
          if (!indication) {
            l.type = 'Saldo';
            l.payment_method = '-';
          } else if (indication && l.type === 'WITHDRAW') {
            (l.type = 'Agendamento de consulta'),
              (l.payment_method = 'Carteira');
          } else if (indication && l.id === null) {
            l.type = 'Agendamento de consulta';
            l.payment_method = 'Link gerado';
          } else if (l.type === 'REFOUND') {
            l.type = 'Agendamento de consulta';
            l.status = 'REFOUND';
            l.payment_method = 'Carteira';
          }
        }

        this.analyticReportListFull.push(...list);
        this.analyticReportList = this.analyticReportListFull.slice(
          0,
          this.maxLimit
        );
        this.btnUpdate();
        this.isLoading = {
          message: '',
          active: false,
        };
      },
      error: (err) => {
        this.isLoading = {
          message: err.message,
          active: true,
        };
      },
    });
  }

  private generateDepositWalletBillingLink(
    amountRequested: number,
    method: string
  ): void {
    const body = {
      customer: {
        accountId: this.accountId,
        name: this.account.name,
        document: this.account?.cpf ? this.account.cpf : this.account.cnpj,
        email: this.account.email,
      },
      billing: {
        amount: amountRequested,
        description: `Recarga de saldo.`,
        type: method,
      },
    };

    this.paymentService.generateDepositWalletBillingLink(body).subscribe({
      next: (data) => {
        this.generatedLink = data.link;
        window.open(this.generatedLink, '_blank');
      },
    });
  }

  private refreshComponent = () => {
    if (document.visibilityState === 'visible') {
      this.refreshFindNewItem();
    }
  };

  private refreshFindNewItem(): void {
    this.getUserWalletAmmount();

    this.transactions
      .analyticReportTransactions({ limit: 1, skip: 0 })
      .subscribe({
        next: (data) => {
          if (data.length <= 0) return;

          const newItem = this.fixResponseStructure(data);
          const isFound = this.analyticReportList.some(
            (i) => i.id === newItem[0].id
          );
          if (!isFound) {
            newItem[0].type = 'Saldo';
            this.analyticReportListFull.unshift(newItem[0]);
            this.analyticReportList.unshift(newItem[0]);
            this.btnUpdate();
            this.isLoading = {
              message: '',
              active: false,
            };
          }

          //Atualiza o valor do primeiro item da lista caso tenha feito o pagamento imediato
          const item = this.analyticReportList.find(
            (i) => i.id === newItem[0].id
          );

          if (item && item.status !== 'REFOUND') {
            item.status = newItem[0].status;
          }
        },
      });
  }

  private btnUpdate(): void {
    this.btnShowMoreAnalytic =
      this.analyticReportList.length < this.analyticReportListFull.length;
  }

  private fixResponseStructure(
    items: AnalyticReportDto[]
  ): AnalyticReportFixed[] {
    const list = [];
    const item = items.map((item) => {
      return {
        id: item.w_t_id || item.w_t2_id,
        indicationId: item.t_indication_id,
        amount: item.t_amount || item.w_t3_amount,
        type: item.w_t_type || item.w_t2_type,
        status: item.t_status || item.w_t_status || item.w_t2_status,
        createAt: item.t_created_at,
        paymentLink: item.t_payment_link,
        refundId: item.w_t3_id,
        refundAmount: item.w_t3_amount,
        typeRefund: item.w_t3_type,
        statusRefund: item.w_t3_status,
        createAtRefund: item.w_t3_createdAt,
        patientName: item.patient_name,
        documentUrl: item.documentUrl,
        attendanceStatus: item.attendanceStatus,
      } as AnalyticReportFixed;
    });

    for (let i of item) {
      list.push(i);
      if (i.refundId > 0) {
        const item = {
          id: i.refundId,
          indicationId: i.indicationId,
          amount: i.refundAmount,
          type: i.typeRefund,
          status: i.statusRefund,
          createAt: i.createAtRefund,
          paymentLink: i.paymentLink,
          patientName: i.patientName,
        };
        list.push(item);
      }
    }
    const sortedList = list.sort(
      (a, b) => new Date(b.createAt).getTime() - new Date(a.createAt).getTime()
    );

    return sortedList;
  }

  private getUserWalletAmmount(): void {
    this.accountService
      .getProfile()
      .pipe(
        switchMap((data) => {
          this.account = data.account;
          return this.walletService.getAmount({ accountId: data.account.id });
        })
      )
      .subscribe({
        next: (walletAmount) => {
          this.totalAmount = walletAmount.amount;
        },
        error: (err) => console.log('Erro ao receber dados da carteira.', err),
      });
  }
}
